import { useEffect, useReducer } from 'react';
import {
  fetchWorkspacePolicyCheck,
  isClientDisabledFailure,
  type PolicyCheckSuccess,
} from '../../shared/agent-consent/domain/policyClient';
import {
  getToolsetNames,
  mergeMcpPolicyIntents,
  parseMcpPolicyData,
} from './policyParse';
import {
  getPolicyRequestKey,
  getPolicyStateForRequest,
  MCP_POLICY_ERROR_KIND,
  policyReducer,
  type PolicyState,
} from './policyState';
import type { TrelloWorkspace } from './trelloTypes';

import type { ConsentToken } from '#definitions/context';

export type UseTrelloPolicyParams = {
  token: ConsentToken;
  /** False keeps the check idle: no Trello in this flow, or Agent MCP. */
  isEnabled: boolean;
  /**
   * The ids to check, one /consent/workspace request each — the selected
   * workspaces, or the account-level target (see useTrelloSelection).
   */
  targets: readonly string[];
  /** The selected workspaces, to name one in a failure. */
  selectedTrelloWorkspaces: readonly TrelloWorkspace[];
  onToolsetsLoaded: (toolsetNames: Set<string>) => void;
};

export type TrelloPolicy = {
  policy: PolicyState;
  /**
   * The token asked for Inbox/Planner but a selected workspace is outside the
   * user's managing organization, so the check removed those scopes.
   */
  inboxPlannerScopesDisabled: boolean;
};

/**
 * Policy for the Trello targets: every check runs at once, the first failure
 * (in target order) is reported for the whole source, and the toolsets of a
 * fully successful set are merged. Uses the same /consent/workspace endpoint
 * as the site check, with the Trello workspace ARI as `selectedWorkspace`.
 */
export function useTrelloPolicy({
  token,
  isEnabled,
  targets,
  selectedTrelloWorkspaces,
  onToolsetsLoaded,
}: UseTrelloPolicyParams): TrelloPolicy {
  const [policyState, dispatch] = useReducer(policyReducer, {
    status: 'idle',
  });
  const requestKey =
    isEnabled && targets.length > 0
      ? getPolicyRequestKey(token.raw, targets)
      : null;

  useEffect(() => {
    if (token.serviceType !== 'MCP') return;

    if (requestKey == null) {
      dispatch({ type: 'RESET' });
      return;
    }

    const ac = new AbortController();
    dispatch({ type: 'LOADING', requestKey });

    void (async () => {
      try {
        const results = await Promise.all(
          targets.map((target) =>
            fetchWorkspacePolicyCheck(
              { contextJwt: token.raw, selectedWorkspace: target },
              ac.signal,
            ),
          ),
        );

        if (ac.signal.aborted) return;

        const failedIndex = results.findIndex((result) => !result.ok);
        const failedResult = failedIndex === -1 ? null : results[failedIndex];
        if (failedResult && !failedResult.ok) {
          const clientDisabled = isClientDisabledFailure(failedResult);
          const accessRevoked = !clientDisabled && failedResult.status === 403;
          dispatch({
            type: 'ERROR',
            requestKey,
            errorMessage: failedResult.errorMessage,
            errorKind: clientDisabled
              ? MCP_POLICY_ERROR_KIND.clientDisabled
              : accessRevoked
                ? MCP_POLICY_ERROR_KIND.workspaceAccessRevoked
                : MCP_POLICY_ERROR_KIND.policyFetchFailed,
            errorWorkspaceName:
              clientDisabled || accessRevoked
                ? (selectedTrelloWorkspaces[failedIndex]?.displayName ?? null)
                : null,
          });
          return;
        }

        const successfulResults = results.filter(
          (result): result is PolicyCheckSuccess => result.ok,
        );

        if (successfulResults.some((result) => !result.data.redirectAllowed)) {
          dispatch({
            type: 'ERROR',
            requestKey,
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          return;
        }

        const parsedPolicyData = successfulResults.map((result) =>
          parseMcpPolicyData(result.data),
        );
        const byIntent = mergeMcpPolicyIntents(
          parsedPolicyData.flatMap((data) => data.byIntent),
        );
        dispatch({
          type: 'SUCCESS',
          requestKey,
          byIntent,
          crossOrgScopesRemoved: parsedPolicyData.some(
            (data) => data.crossOrgScopesRemoved,
          ),
        });
        // Pre-select Trello toolsets, adding to existing selection
        onToolsetsLoaded(getToolsetNames(byIntent));
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatch({
          type: 'ERROR',
          requestKey,
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [token, requestKey, targets, selectedTrelloWorkspaces, onToolsetsLoaded]);

  const policy = getPolicyStateForRequest(policyState, requestKey);
  return {
    policy,
    inboxPlannerScopesDisabled:
      isEnabled &&
      selectedTrelloWorkspaces.length > 0 &&
      policy.status === 'success' &&
      policy.crossOrgScopesRemoved,
  };
}
